import type { Tables } from '@/integrations/supabase/types';
import { type BillingStructure, type Role, type RateMultiplier, type MealBreakConfig, type OvernightConfig } from '@/types';
import type { BreakBillingPolicy } from '@/types/MealBreakConfig';
import type { ExpenseCategory } from '@/types/ExpenseCategory';
import type { ExpensePolicies } from '@/types/ExpenseConfig';
import type { GearItem, GearKit, CartageConfig } from '@/types/GearConfig';
import type { UserConfigurationState } from '@/types/userConfiguration';

/** Sections tracked for customization detection in the client form. */
export type CustomizedSections = {
  billing: boolean;
  breaks: boolean;
  invoicing: boolean;
  roles: boolean;
  multipliers: boolean;
  shortTurn: boolean;
  holiday: boolean;
  mealBreak: boolean;
  overnight: boolean;
  expenses: boolean;
  gear: boolean;
};

// Use the auto-generated type for the client configuration view
export type ClientFormData = Omit<Partial<Tables<'client_configuration'>>, 'roles' | 'rate_multipliers'> & {
  roles?: Role[];
  rate_multipliers?: RateMultiplier[];
  catalog_holiday_ids?: string[];
  custom_holiday_ids?: string[];
  // Re-add the billing structure fields here since they are part of the client_configuration view
  minimum_billable_hours?: number | null;
  half_day_hours?: number | null;
  week_start?: number | null;
  per_week_day_rate_calculation_basis?: 'billable' | 'actual';
  // Additional meal config fields that may not be in the view yet
  surcharge_applies_to_shorted_hours_only?: boolean;
  use_additional_offset?: boolean;
  meal_premium_type?: 'percentage' | 'fixed' | null;
  meal_premium_value?: number | null;
  meal_premium_basis?: string;
  step_up_after_minutes?: number | null;
  fixed_fee_schedule?: MealBreakConfig['fixedFeeSchedule'];
  fixed_fee_amount?: number | null;
  add_fixed_surcharges?: boolean;
  // Meal unified rate-offset fields
  meal_floor_source?: MealBreakConfig['floorSource'];
  meal_overtime_offset?: number | null;
  meal_floor_tier_code?: string | null;
  meal_floor_tier_custom_value?: number | null;
  // Short turn fields (unified rate-offset schema — Phase 9)
  short_turn_premium_type?: 'percentage' | 'fixed' | null;
  short_turn_premium_value?: number | null;
  short_turn_premium_basis?: 'base_rate' | 'ot_inclusive';
  short_turn_apply_on_half_days?: boolean;
  short_turn_applicable_role_ids?: string[] | null;
  // Holiday premium fields (unified rate-offset schema — Phase 9)
  holiday_hourly_premium_type?: 'percentage' | 'fixed' | null;
  holiday_hourly_premium_value?: number | null;
  holiday_premium_basis?: 'base_rate' | 'ot_inclusive';
  meal_apply_on_half_days?: boolean;
  holiday_applicable_role_ids?: string[] | null;
  meal_applicable_role_ids?: string[] | null;
  holiday_apply_on_half_days?: boolean;
  // Overnight Premium fields
  apply_overnight_premium?: boolean;
  overnight_start_time?: string | null;
  overnight_end_time?: string | null;
  overnight_premium_type?: OvernightConfig['overnightMethod'];
  overnight_threshold_enabled?: boolean;
  overnight_threshold_type?: 'percentage' | 'hours' | null;
  overnight_percentage_threshold?: number | null;
  overnight_hours_threshold?: number | null;
  overnight_hourly_premium_type?: 'percentage' | 'fixed' | null;
  overnight_hourly_premium_value?: number | null;
  overnight_hourly_premium_basis?: 'base_rate' | 'ot_inclusive';
  overnight_flat_rate_amount?: number | null;
  overnight_time_credit_minutes?: number | null;
  overnight_applicable_role_ids?: string[] | null;
  overnight_apply_on_half_days?: boolean;
  // Overnight unified rate-offset fields
  overnight_floor_source?: OvernightConfig['floorSource'];
  overnight_overtime_offset?: number | null;
  overnight_floor_tier_code?: string | null;
  overnight_floor_tier_custom_value?: number | null;
  // Expense fields
  expense_policies?: ExpensePolicies;
  expense_categories?: ExpenseCategory[];
  expense_mileage_rate?: number | null;
  expense_mileage_unit?: 'miles' | 'km';
  default_expense_markup_type?: 'none' | 'percentage' | 'fixed_amount';
  default_expense_markup_percentage?: number | null;
  default_expense_markup_fixed_amount?: number | null;
  expense_receipt_required_above_amount?: number | null;
  // Travel distance threshold
  travel_distance_threshold?: number | null;
  // Gear/Kit fees
  gear_items?: GearItem[];
  gear_kits?: GearKit[];
  cartage?: CartageConfig;
  gear_week_length_days?: number | null;
  // Override Json type from DB with proper TS type
  break_billing_policy?: BreakBillingPolicy | null;
};

// Centralized section field definitions - single source of truth
export const SECTION_FIELDS: Record<string, (keyof ClientFormData)[]> = {
  dayRate: [
    'minimum_billable_hours', 'half_day_hours', 'week_start',
    'per_week_day_rate_calculation_basis'
  ],
  travel: [
    'per_diem_base_rate', 'travel_day_rate_multiplier', 'multiplier_applies_to_overages', 
    'uses_gsa_rate'
  ],
  breaks: [
    'meal_break_length_minutes', 'break_billing_policy'
  ],
  invoicing: [
    'payment_terms_days', 'use_late_fee', 'late_fee_type', 'late_fee_percentage', 
    'late_fee_fixed_amount', 'recurring_late_fee', 'recurring_late_fee_interval', 
    'use_early_payment_discount', 'early_payment_type', 'early_payment_discount_percentage', 
    'early_payment_fixed_amount', 'early_payment_days', 'use_cancellation_policy',
    'cancellation_policy', 'rush_premium_policy', 'auto_send_reminders', 'reminder_days', 'employment_type'
  ],
  roles: ['roles'],
  multipliers: ['rate_multipliers'],
  shortTurn: [
    'uses_short_turn', 'required_break_hours', 'short_turn_surcharge_type',
    'short_turn_fixed_surcharge_amount', 'short_turn_overtime_offset', 'surcharge_applies_to_shorted_hours_only',
    'short_turn_premium_type', 'short_turn_floor_tier_code', 'short_turn_floor_tier_custom_value', 'short_turn_premium_value', 'short_turn_premium_basis',
    'short_turn_apply_on_half_days'
  ],
  holiday: [
    'apply_holiday_premium', 'holiday_premium_type',
    'holiday_overtime_offset', 'holiday_fixed_premium_amount',
    'holiday_floor_tier_code', 'holiday_floor_tier_custom_value',
    'holiday_hourly_premium_type', 'holiday_premium_basis',
    'holiday_hourly_premium_value', 'holiday_apply_on_half_days',
    'surcharge_concurrency_strategy', 'add_fixed_surcharges'
  ],
  mealBreak: [
    'apply_meal_surcharge', 'meal_deadline_hours', 'recurring_meal_deadline',
    'grace_period_minutes', 'meal_surcharge_type', 'use_additional_offset', 'credit_interval_minutes', 'meal_premium_basis',
    'time_credit_minutes', 'step_up_after_minutes', 'fixed_fee_schedule',
    'meal_premium_type', 'meal_premium_value', 'meal_premium_stacking', 'meal_apply_on_half_days',
    'meal_floor_source', 'meal_overtime_offset', 'meal_floor_tier_code',
    'meal_floor_tier_custom_value',
    'meal_extended_overtime_enabled', 'meal_extended_overtime_tiers',
    'meal_trigger_mode', 'required_breaks', 'threshold_hours',
    'additional_break_interval_hours', 'required_breaks_half_day'
  ],
  overnight: [
    'apply_overnight_premium', 'overnight_start_time', 'overnight_end_time', 'overnight_hourly_premium_basis',
    'overnight_premium_type', 'overnight_hourly_premium_type', 'overnight_hourly_premium_value',
    'overnight_flat_rate_amount', 'overnight_time_credit_minutes', 'overnight_threshold_enabled',
    'overnight_threshold_type', 'overnight_percentage_threshold', 'overnight_hours_threshold',
    'overnight_apply_on_half_days',
    'overnight_floor_source', 'overnight_overtime_offset', 'overnight_floor_tier_code',
    'overnight_floor_tier_custom_value'
  ],
  expenses: [
    'expense_policies', 'expense_categories', 'expense_mileage_rate', 'expense_mileage_unit',
    'default_expense_markup_type', 'default_expense_markup_percentage',
    'default_expense_markup_fixed_amount', 'expense_receipt_required_above_amount'
  ],
  gear: [
    'gear_items', 'gear_kits', 'cartage', 'gear_week_length_days'
  ],
};

export const initializeFormDataFromDefaults = (
  clientData: ClientFormData,
  userDefaults: UserConfigurationState
): ClientFormData => {
  return {
    name: clientData.name ?? '',
    description: clientData.description,
    domain: clientData.domain,
    physical_address: clientData.physical_address,
    billing_address: clientData.billing_address,
    // Billing & Billing Structure
    minimum_billable_hours: userDefaults.billingStructureConfig?.minimumBillableHours,
    half_day_hours: userDefaults.billingStructureConfig?.halfDayHours,
    week_start: userDefaults.billingStructureConfig?.weekStart,
    per_week_day_rate_calculation_basis: userDefaults.billingStructureConfig?.perWeekDayRateCalculationBasis ?? undefined,
    uses_short_turn: userDefaults.usesShortTurn,
    allow_premium_shifts: userDefaults.allowPremiumShifts,
    premium_shift_offset_mode: userDefaults.premiumShiftOffsetMode,
    premium_shift_fixed_offset: userDefaults.premiumShiftFixedOffset,
    premium_shift_multiplier_code: userDefaults.premiumShiftMultiplierCode,
    // Holiday Premium
    apply_holiday_premium: userDefaults.applyHolidayPremium,
    catalog_holiday_ids: userDefaults.holidayCatalogIds ? [...userDefaults.holidayCatalogIds] : [],
    custom_holiday_ids: userDefaults.holidayCustomIds ? [...userDefaults.holidayCustomIds] : [],
    holiday_premium_type: userDefaults.holidayPremiumType,
    holiday_overtime_offset: userDefaults.holidayOvertimeOffset,
    holiday_floor_source: userDefaults.holidayFloorSource,
    holiday_floor_tier_code: userDefaults.holidayFloorTierCode,
    holiday_floor_tier_custom_value: userDefaults.holidayFloorTierCustomValue,
    holiday_fixed_premium_amount: userDefaults.holidayFixedPremiumAmount,
    holiday_hourly_premium_type: userDefaults.holidayHourlyPremiumType,
    holiday_hourly_premium_value: userDefaults.holidayHourlyPremiumValue,
    holiday_premium_basis: userDefaults.holidayPremiumBasis,
    holiday_apply_on_half_days: userDefaults.holidayApplyOnHalfDays,
    surcharge_concurrency_strategy: userDefaults.surchargeConcurrencyStrategy,
    add_fixed_surcharges: userDefaults.addFixedSurcharges,
    // Invoicing
    payment_terms_days: userDefaults.paymentTermsDays,
    employment_type: userDefaults.employmentType,
    auto_send_reminders: userDefaults.autoSendReminders,
    use_late_fee: userDefaults.useLateFee,
    late_fee_type: userDefaults.lateFeeType,
    late_fee_percentage: userDefaults.lateFeePercentage,
    late_fee_fixed_amount: userDefaults.lateFeeFixedAmount,
    recurring_late_fee: userDefaults.recurringLateFee,
    recurring_late_fee_interval: userDefaults.recurringLateFeeInterval,
    use_early_payment_discount: userDefaults.useEarlyPaymentDiscount,
    early_payment_type: userDefaults.earlyPaymentType,
    early_payment_discount_percentage: userDefaults.earlyPaymentDiscountPercentage,
    early_payment_fixed_amount: userDefaults.earlyPaymentFixedAmount,
    early_payment_days: userDefaults.earlyPaymentDays,
    use_cancellation_policy: userDefaults.useCancellationPolicy,
    cancellation_policy: userDefaults.cancellationPolicy,
    rush_premium_policy: userDefaults.rushPremiumPolicy,
    reminder_days: userDefaults.reminderDays,
    // Short Turn
    required_break_hours: userDefaults.shortTurnConfig?.requiredBreakHours,
    short_turn_surcharge_type: userDefaults.shortTurnConfig?.surchargeType,
    short_turn_fixed_surcharge_amount: userDefaults.shortTurnConfig?.shortTurnFixedSurchargeAmount,
    short_turn_overtime_offset: userDefaults.shortTurnConfig?.overtimeOffset,
    short_turn_floor_source: userDefaults.shortTurnConfig?.floorSource,
    short_turn_floor_tier_code: userDefaults.shortTurnConfig?.floorTierCode,
    short_turn_floor_tier_custom_value: userDefaults.shortTurnConfig?.floorTierCustomValue,
    surcharge_applies_to_shorted_hours_only: userDefaults.shortTurnConfig?.surchargeAppliesToShortedHoursOnly,
    short_turn_premium_type: userDefaults.shortTurnConfig?.premiumType,
    short_turn_premium_value: userDefaults.shortTurnConfig?.premiumValue,
    short_turn_premium_basis: userDefaults.shortTurnConfig?.premiumBasis,
    short_turn_apply_on_half_days: userDefaults.shortTurnConfig?.applyOnHalfDays,
    // Multipliers
    rate_multipliers: userDefaults.rateMultipliers ? JSON.parse(JSON.stringify(userDefaults.rateMultipliers)) : [],
    // Travel
    per_diem_base_rate: userDefaults.perDiemBaseRate,
    travel_day_rate_multiplier: userDefaults.travelDayRateMultiplier,
    multiplier_applies_to_overages: userDefaults.multiplierAppliesToOverages,
    uses_gsa_rate: userDefaults.usesGsaRate,
    // Meal Break
    apply_meal_surcharge: userDefaults.mealConfig?.applyMealSurcharge,
    meal_deadline_hours: userDefaults.mealConfig?.mealDeadlineHours,
    recurring_meal_deadline: userDefaults.mealConfig?.recurringMealDeadline,
    grace_period_minutes: userDefaults.mealConfig?.gracePeriodMinutes,
    meal_surcharge_type: userDefaults.mealConfig?.mealSurchargeType,
    use_additional_offset: userDefaults.mealConfig?.useAdditionalOffset,
    meal_premium_type: userDefaults.mealConfig?.premiumType,
    meal_premium_value: userDefaults.mealConfig?.premiumValue,
    meal_premium_basis: userDefaults.mealConfig?.premiumBasis,
    meal_premium_stacking: userDefaults.mealConfig?.premiumStacking,
    credit_interval_minutes: userDefaults.mealConfig?.creditIntervalMinutes,
    time_credit_minutes: userDefaults.mealConfig?.timeCreditMinutes,
    meal_apply_on_half_days: userDefaults.mealConfig?.applyOnHalfDays,
    step_up_after_minutes: userDefaults.mealConfig?.stepUpAfterMinutes,
    fixed_fee_schedule: userDefaults.mealConfig?.fixedFeeSchedule ? JSON.parse(JSON.stringify(userDefaults.mealConfig.fixedFeeSchedule)) : null,
    fixed_fee_amount: userDefaults.mealConfig?.fixedFeeAmount ?? null,
    meal_floor_source: userDefaults.mealConfig?.floorSource,
    meal_overtime_offset: userDefaults.mealConfig?.overtimeOffset,
    meal_floor_tier_code: userDefaults.mealConfig?.floorTierCode,
    meal_floor_tier_custom_value: userDefaults.mealConfig?.floorTierCustomValue,
    meal_extended_overtime_enabled: userDefaults.mealConfig?.extendedOvertimeEnabled ?? false,
    meal_extended_overtime_tiers: userDefaults.mealConfig?.extendedOvertimeTiers
      ? (JSON.parse(JSON.stringify(userDefaults.mealConfig.extendedOvertimeTiers)) as ClientFormData['meal_extended_overtime_tiers'])
      : [] as unknown as ClientFormData['meal_extended_overtime_tiers'],
    meal_trigger_mode: userDefaults.mealConfig?.mealTriggerMode,
    required_breaks: userDefaults.mealConfig?.requiredBreaks,
    threshold_hours: userDefaults.mealConfig?.thresholdHours,
    additional_break_interval_hours: userDefaults.mealConfig?.additionalBreakIntervalHours,
    required_breaks_half_day: userDefaults.mealConfig?.requiredBreaksHalfDay,
    // Break settings (from mealConfig)
    meal_break_length_minutes: userDefaults.mealConfig?.mealBreakLengthMinutes,
    break_billing_policy: userDefaults.mealConfig?.breakBillingPolicy,
    // Overnight
    apply_overnight_premium: userDefaults.overnightConfig?.applyOvernightPremium,
    overnight_start_time: userDefaults.overnightConfig?.startTime,
    overnight_end_time: userDefaults.overnightConfig?.endTime,
    overnight_threshold_enabled: userDefaults.overnightConfig?.thresholdEnabled,
    overnight_threshold_type: userDefaults.overnightConfig?.thresholdType,
    overnight_percentage_threshold: userDefaults.overnightConfig?.percentageThreshold,
    overnight_hours_threshold: userDefaults.overnightConfig?.hoursThreshold,
    overnight_premium_type: userDefaults.overnightConfig?.overnightMethod,
    overnight_hourly_premium_type: userDefaults.overnightConfig?.premiumType,
    overnight_hourly_premium_value: userDefaults.overnightConfig?.premiumValue,
    overnight_hourly_premium_basis: userDefaults.overnightConfig?.premiumBasis,
    overnight_flat_rate_amount: userDefaults.overnightConfig?.flatRateAmount,
    overnight_time_credit_minutes: userDefaults.overnightConfig?.timeCreditMinutes,
    overnight_apply_on_half_days: userDefaults.overnightConfig?.applyOnHalfDays,
    overnight_floor_source: userDefaults.overnightConfig?.floorSource,
    overnight_overtime_offset: userDefaults.overnightConfig?.overtimeOffset,
    overnight_floor_tier_code: userDefaults.overnightConfig?.floorTierCode,
    overnight_floor_tier_custom_value: userDefaults.overnightConfig?.floorTierCustomValue,
    // Roles
    roles: userDefaults.roles ? [...userDefaults.roles] : [],
    // Expenses
    expense_policies: userDefaults.expenseConfig?.expensePolicies,
    expense_categories: userDefaults.expenseConfig?.expenseCategories as ClientFormData['expense_categories'],
    expense_mileage_rate: userDefaults.expenseConfig?.expenseMileageRate,
    expense_mileage_unit: userDefaults.expenseConfig?.expenseMileageUnit ?? undefined,
    default_expense_markup_type: userDefaults.expenseConfig?.defaultExpenseMarkupType ?? undefined,
    default_expense_markup_percentage: userDefaults.expenseConfig?.defaultExpenseMarkupPercentage,
    default_expense_markup_fixed_amount: userDefaults.expenseConfig?.defaultExpenseMarkupFixedAmount,
    expense_receipt_required_above_amount: userDefaults.expenseConfig?.expenseReceiptRequiredAboveAmount,
    // Travel
    travel_distance_threshold: userDefaults.travelDistanceThreshold,
    // Gear
    gear_items: userDefaults.gearConfig?.gearItems ? JSON.parse(JSON.stringify(userDefaults.gearConfig.gearItems)) : [],
    gear_kits: userDefaults.gearConfig?.gearKits ? JSON.parse(JSON.stringify(userDefaults.gearConfig.gearKits)) : [],
    cartage: userDefaults.gearConfig?.cartage ? JSON.parse(JSON.stringify(userDefaults.gearConfig.cartage)) : { method: 'none' },
    gear_week_length_days: userDefaults.gearConfig?.weekLengthDays ?? 5,
  };
};

export const getDefaultsToRestore = (
  section: 'dayRate' | 'travel' | 'breaks' | 'invoice' | 'roles' | 'multipliers' | 'shortTurn' | 'holiday' | 'mealBreak' | 'overnight' | 'expenses' | 'gear',
  userDefaults: UserConfigurationState
): Partial<ClientFormData> => {
  switch (section) {
    case 'dayRate':
      return {
        minimum_billable_hours: userDefaults.billingStructureConfig?.minimumBillableHours,
        half_day_hours: userDefaults.billingStructureConfig?.halfDayHours,
        week_start: userDefaults.billingStructureConfig?.weekStart,
        per_week_day_rate_calculation_basis: userDefaults.billingStructureConfig?.perWeekDayRateCalculationBasis ?? undefined,
      };
    case 'travel':
      return {
        per_diem_base_rate: userDefaults.perDiemBaseRate,
        travel_day_rate_multiplier: userDefaults.travelDayRateMultiplier,
        multiplier_applies_to_overages: userDefaults.multiplierAppliesToOverages,
        uses_gsa_rate: userDefaults.usesGsaRate,
      };
    case 'breaks':
      return {
        meal_break_length_minutes: userDefaults.mealConfig?.mealBreakLengthMinutes,
        break_billing_policy: userDefaults.mealConfig?.breakBillingPolicy,
      };
    case 'invoice':
      return {
        payment_terms_days: userDefaults.paymentTermsDays,
        use_late_fee: userDefaults.useLateFee,
        late_fee_type: userDefaults.lateFeeType,
        late_fee_percentage: userDefaults.lateFeePercentage,
        late_fee_fixed_amount: userDefaults.lateFeeFixedAmount,
        recurring_late_fee: userDefaults.recurringLateFee,
        recurring_late_fee_interval: userDefaults.recurringLateFeeInterval,
        use_early_payment_discount: userDefaults.useEarlyPaymentDiscount,
        early_payment_type: userDefaults.earlyPaymentType,
        early_payment_discount_percentage: userDefaults.earlyPaymentDiscountPercentage,
        early_payment_fixed_amount: userDefaults.earlyPaymentFixedAmount,
        early_payment_days: userDefaults.earlyPaymentDays,
        use_cancellation_policy: userDefaults.useCancellationPolicy,
        cancellation_policy: userDefaults.cancellationPolicy,
        rush_premium_policy: userDefaults.rushPremiumPolicy,
        auto_send_reminders: userDefaults.autoSendReminders,
        reminder_days: userDefaults.reminderDays,
        employment_type: userDefaults.employmentType,
      };
    case 'roles':
      return {
        roles: userDefaults.roles ? [...userDefaults.roles] : [],
      };
    case 'multipliers':
      return {
        rate_multipliers: userDefaults.rateMultipliers ? [...userDefaults.rateMultipliers] : [],
        week_start: userDefaults.billingStructureConfig?.weekStart,
      };
    case 'shortTurn':
      return {
        uses_short_turn: userDefaults.usesShortTurn,
        required_break_hours: userDefaults.shortTurnConfig?.requiredBreakHours,
        short_turn_surcharge_type: userDefaults.shortTurnConfig?.surchargeType,
        short_turn_fixed_surcharge_amount: userDefaults.shortTurnConfig?.shortTurnFixedSurchargeAmount,
        short_turn_overtime_offset: userDefaults.shortTurnConfig?.overtimeOffset,
        short_turn_floor_source: userDefaults.shortTurnConfig?.floorSource,
        short_turn_floor_tier_code: userDefaults.shortTurnConfig?.floorTierCode,
        short_turn_floor_tier_custom_value: userDefaults.shortTurnConfig?.floorTierCustomValue,
        surcharge_applies_to_shorted_hours_only: userDefaults.shortTurnConfig?.surchargeAppliesToShortedHoursOnly,
        short_turn_premium_type: userDefaults.shortTurnConfig?.premiumType,
        short_turn_premium_value: userDefaults.shortTurnConfig?.premiumValue,
        short_turn_premium_basis: userDefaults.shortTurnConfig?.premiumBasis,
        short_turn_apply_on_half_days: userDefaults.shortTurnConfig?.applyOnHalfDays,
      };
    case 'holiday':
      return {
        apply_holiday_premium: userDefaults.applyHolidayPremium,
        catalog_holiday_ids: userDefaults.holidayCatalogIds ? [...userDefaults.holidayCatalogIds] : [],
        custom_holiday_ids: userDefaults.holidayCustomIds ? [...userDefaults.holidayCustomIds] : [],
        holiday_premium_type: userDefaults.holidayPremiumType,
        holiday_overtime_offset: userDefaults.holidayOvertimeOffset,
        holiday_floor_source: userDefaults.holidayFloorSource,
        holiday_floor_tier_code: userDefaults.holidayFloorTierCode,
        holiday_floor_tier_custom_value: userDefaults.holidayFloorTierCustomValue,
        holiday_fixed_premium_amount: userDefaults.holidayFixedPremiumAmount,
        holiday_hourly_premium_type: userDefaults.holidayHourlyPremiumType,
        holiday_hourly_premium_value: userDefaults.holidayHourlyPremiumValue,
        holiday_premium_basis: userDefaults.holidayPremiumBasis,
        holiday_apply_on_half_days: userDefaults.holidayApplyOnHalfDays,
        surcharge_concurrency_strategy: userDefaults.surchargeConcurrencyStrategy,
        add_fixed_surcharges: userDefaults.addFixedSurcharges,
      };
    case 'mealBreak':
      return {
        apply_meal_surcharge: userDefaults.mealConfig?.applyMealSurcharge,
        meal_deadline_hours: userDefaults.mealConfig?.mealDeadlineHours,
        recurring_meal_deadline: userDefaults.mealConfig?.recurringMealDeadline,
        meal_break_length_minutes: userDefaults.mealConfig?.mealBreakLengthMinutes,
        grace_period_minutes: userDefaults.mealConfig?.gracePeriodMinutes,
        break_billing_policy: userDefaults.mealConfig?.breakBillingPolicy,
        meal_surcharge_type: userDefaults.mealConfig?.mealSurchargeType,
        use_additional_offset: userDefaults.mealConfig?.useAdditionalOffset,
        meal_premium_type: userDefaults.mealConfig?.premiumType,
        meal_premium_value: userDefaults.mealConfig?.premiumValue,
        meal_premium_basis: userDefaults.mealConfig?.premiumBasis,
        meal_premium_stacking: userDefaults.mealConfig?.premiumStacking,
        credit_interval_minutes: userDefaults.mealConfig?.creditIntervalMinutes,
        meal_apply_on_half_days: userDefaults.mealConfig?.applyOnHalfDays,
        time_credit_minutes: userDefaults.mealConfig?.timeCreditMinutes,
        step_up_after_minutes: userDefaults.mealConfig?.stepUpAfterMinutes,
        fixed_fee_schedule: userDefaults.mealConfig?.fixedFeeSchedule,
        fixed_fee_amount: userDefaults.mealConfig?.fixedFeeAmount,
        meal_floor_source: userDefaults.mealConfig?.floorSource,
        meal_overtime_offset: userDefaults.mealConfig?.overtimeOffset,
        meal_floor_tier_code: userDefaults.mealConfig?.floorTierCode,
        meal_floor_tier_custom_value: userDefaults.mealConfig?.floorTierCustomValue,
        meal_extended_overtime_enabled: userDefaults.mealConfig?.extendedOvertimeEnabled ?? false,
        meal_extended_overtime_tiers: userDefaults.mealConfig?.extendedOvertimeTiers
          ? (JSON.parse(JSON.stringify(userDefaults.mealConfig.extendedOvertimeTiers)) as ClientFormData['meal_extended_overtime_tiers'])
          : [] as unknown as ClientFormData['meal_extended_overtime_tiers'],
        meal_trigger_mode: userDefaults.mealConfig?.mealTriggerMode,
        required_breaks: userDefaults.mealConfig?.requiredBreaks,
        threshold_hours: userDefaults.mealConfig?.thresholdHours,
        additional_break_interval_hours: userDefaults.mealConfig?.additionalBreakIntervalHours,
        required_breaks_half_day: userDefaults.mealConfig?.requiredBreaksHalfDay,
      };
    case 'overnight':
      return {
        apply_overnight_premium: userDefaults.overnightConfig?.applyOvernightPremium,
        overnight_start_time: userDefaults.overnightConfig?.startTime,
        overnight_end_time: userDefaults.overnightConfig?.endTime,
        overnight_premium_type: userDefaults.overnightConfig?.overnightMethod,
        overnight_threshold_enabled: userDefaults.overnightConfig?.thresholdEnabled,
        overnight_threshold_type: userDefaults.overnightConfig?.thresholdType,
        overnight_percentage_threshold: userDefaults.overnightConfig?.percentageThreshold,
        overnight_hours_threshold: userDefaults.overnightConfig?.hoursThreshold,
        overnight_hourly_premium_type: userDefaults.overnightConfig?.premiumType,
        overnight_hourly_premium_value: userDefaults.overnightConfig?.premiumValue,
        overnight_hourly_premium_basis: userDefaults.overnightConfig?.premiumBasis,
        overnight_flat_rate_amount: userDefaults.overnightConfig?.flatRateAmount,
        overnight_time_credit_minutes: userDefaults.overnightConfig?.timeCreditMinutes,
        overnight_apply_on_half_days: userDefaults.overnightConfig?.applyOnHalfDays,
        overnight_floor_source: userDefaults.overnightConfig?.floorSource,
        overnight_overtime_offset: userDefaults.overnightConfig?.overtimeOffset,
        overnight_floor_tier_code: userDefaults.overnightConfig?.floorTierCode,
        overnight_floor_tier_custom_value: userDefaults.overnightConfig?.floorTierCustomValue,
      };
    case 'expenses':
      return {
        expense_policies: userDefaults.expenseConfig?.expensePolicies,
        expense_categories: userDefaults.expenseConfig?.expenseCategories as ClientFormData['expense_categories'],
        expense_mileage_rate: userDefaults.expenseConfig?.expenseMileageRate,
        expense_mileage_unit: userDefaults.expenseConfig?.expenseMileageUnit ?? undefined,
        default_expense_markup_type: userDefaults.expenseConfig?.defaultExpenseMarkupType ?? undefined,
        default_expense_markup_percentage: userDefaults.expenseConfig?.defaultExpenseMarkupPercentage,
        default_expense_markup_fixed_amount: userDefaults.expenseConfig?.defaultExpenseMarkupFixedAmount,
        expense_receipt_required_above_amount: userDefaults.expenseConfig?.expenseReceiptRequiredAboveAmount,
      };
    case 'gear':
      return {
        gear_items: userDefaults.gearConfig?.gearItems ? JSON.parse(JSON.stringify(userDefaults.gearConfig.gearItems)) : [],
        gear_kits: userDefaults.gearConfig?.gearKits ? JSON.parse(JSON.stringify(userDefaults.gearConfig.gearKits)) : [],
        cartage: userDefaults.gearConfig?.cartage ? JSON.parse(JSON.stringify(userDefaults.gearConfig.cartage)) : { method: 'none' },
        gear_week_length_days: userDefaults.gearConfig?.weekLengthDays ?? 5,
      };
  }
  return {};
};

export const sanitizeClientData = (data: Partial<ClientFormData>): Partial<ClientFormData> => {
  const copy = { ...data };

  // ── Multipliers Billing Structure Sanitization ──────────────────────
  const roles = copy.roles || [];
  let uniformBillingStructure: BillingStructure | null = null;
  if (roles.length > 0) {
    const firstStructure = roles[0].billingStructure;
    if (firstStructure && roles.every(r => r.billingStructure === firstStructure)) {
      uniformBillingStructure = firstStructure;
    }
  }

  if (uniformBillingStructure && copy.rate_multipliers) {
    copy.rate_multipliers = copy.rate_multipliers.map(multiplier => {
      const newMultiplier = { ...multiplier };

      if (newMultiplier.defaultPerShiftThresholdHours !== null && newMultiplier.defaultPerShiftThresholdHours !== undefined) {
        newMultiplier.perShiftBillingStructures = [uniformBillingStructure];
      } else {
        newMultiplier.perShiftBillingStructures = [];
      }

      if (newMultiplier.defaultPerWeekThresholdHours !== null && newMultiplier.defaultPerWeekThresholdHours !== undefined) {
        newMultiplier.perWeekBillingStructures = [uniformBillingStructure];
      } else {
        newMultiplier.perWeekBillingStructures = [];
      }

      return newMultiplier;
    });
  }

  // ── Half-Day billing ────────────────────────────────
  // Half-day threshold (hours) stays global; if no role allows half-days, clear the threshold
  const anyRoleAllowsHalfDay = copy.roles?.some(r => r.billingStructure === 'day_rate' && r.allowHalfDay);
  if (!anyRoleAllowsHalfDay) {
    copy.half_day_hours = null;
  }

  // Short-turn surcharge
  if (copy.uses_short_turn === false) {
    copy.required_break_hours = null;
    copy.short_turn_surcharge_type = null;
    copy.short_turn_fixed_surcharge_amount = null;
    // NULL-method preset CHECK requires default unified-offset shape.
    copy.short_turn_floor_source = 'none';
    copy.short_turn_overtime_offset = 0;
    copy.short_turn_floor_tier_code = null;
    copy.short_turn_floor_tier_custom_value = null;
    copy.surcharge_applies_to_shorted_hours_only = false;
    copy.short_turn_premium_type = null;
    copy.short_turn_premium_value = null;
    copy.short_turn_premium_basis = 'base_rate';
    copy.short_turn_apply_on_half_days = true;
  } else if (copy.uses_short_turn === true) {
    if (copy.short_turn_surcharge_type !== 'fixed_dollar' && copy.short_turn_surcharge_type !== 'hourly_premium') {
      copy.short_turn_fixed_surcharge_amount = null;
    }
    // Preset normalizer (matches SQL preset_validity_check for short_turn):
    //   carry_over      → floor_source='previous_shift', overtime_offset=0
    //   bump_up         → floor_source='previous_shift', overtime_offset=1
    //   offset_overtime → floor_source='none',           overtime_offset >= 1
    //   guaranteed_rate → floor_source='fixed_tier',     overtime_offset=0, code OR custom
    //   hourly_premium  → floor_source='none',           overtime_offset=0
    //   fixed_dollar    → floor_source='none',           overtime_offset=0
    switch (copy.short_turn_surcharge_type) {
      case 'carry_over':
        copy.short_turn_floor_source = 'previous_shift';
        copy.short_turn_overtime_offset = 0;
        copy.short_turn_floor_tier_code = null;
        copy.short_turn_floor_tier_custom_value = null;
        break;
      case 'bump_up':
        copy.short_turn_floor_source = 'previous_shift';
        copy.short_turn_overtime_offset = 1;
        copy.short_turn_floor_tier_code = null;
        copy.short_turn_floor_tier_custom_value = null;
        break;
      case 'offset_overtime':
        copy.short_turn_floor_source = 'none';
        if (!copy.short_turn_overtime_offset || copy.short_turn_overtime_offset < 1) {
          copy.short_turn_overtime_offset = 1;
        }
        copy.short_turn_floor_tier_code = null;
        copy.short_turn_floor_tier_custom_value = null;
        break;
      case 'guaranteed_rate':
        copy.short_turn_floor_source = 'fixed_tier';
        copy.short_turn_overtime_offset = 0;
        if (copy.short_turn_floor_tier_code && copy.short_turn_floor_tier_custom_value != null) {
          copy.short_turn_floor_tier_custom_value = null;
        }
        break;
      default:
        copy.short_turn_floor_source = 'none';
        copy.short_turn_overtime_offset = 0;
        copy.short_turn_floor_tier_code = null;
        copy.short_turn_floor_tier_custom_value = null;
    }
    if (copy.short_turn_surcharge_type !== 'hourly_premium') {
      copy.short_turn_premium_type = null;
      copy.short_turn_premium_value = null;
      copy.short_turn_premium_basis = 'base_rate';
    }
  }

  // Overnight Premium
  if (copy.apply_overnight_premium === false) {
    copy.overnight_premium_type = null;
    copy.overnight_threshold_enabled = false;
    copy.overnight_threshold_type = null;
    copy.overnight_percentage_threshold = null;
    copy.overnight_hours_threshold = null;
    copy.overnight_hourly_premium_type = null;
    copy.overnight_hourly_premium_value = null;
    copy.overnight_hourly_premium_basis = 'base_rate';
    copy.overnight_flat_rate_amount = null;
    copy.overnight_time_credit_minutes = null;
    copy.overnight_apply_on_half_days = true;
  } else if (copy.apply_overnight_premium === true) {
    if (copy.overnight_threshold_enabled) {
      if (copy.overnight_threshold_type === 'percentage') {
        copy.overnight_hours_threshold = null;
      } else if (copy.overnight_threshold_type === 'hours') {
        copy.overnight_percentage_threshold = null;
      } else { // if type is somehow null, disable it
        copy.overnight_threshold_enabled = false;
        copy.overnight_percentage_threshold = null;
        copy.overnight_hours_threshold = null;
      }
    } else {
      copy.overnight_threshold_type = null;
      copy.overnight_percentage_threshold = null;
      copy.overnight_hours_threshold = null;
    }
    switch(copy.overnight_premium_type) {
      case 'hourly_premium':
        copy.overnight_flat_rate_amount = null;
        copy.overnight_time_credit_minutes = null;
        break;
      case 'flat_rate':
        copy.overnight_hourly_premium_type = null;
        copy.overnight_hourly_premium_value = null;
        copy.overnight_hourly_premium_basis = 'base_rate';
        copy.overnight_time_credit_minutes = null;
        break;
      case 'offset_overtime':
        copy.overnight_hourly_premium_type = null;
        copy.overnight_hourly_premium_value = null;
        copy.overnight_hourly_premium_basis = 'base_rate';
        copy.overnight_flat_rate_amount = null;
        copy.overnight_time_credit_minutes = null;
        break;
      case 'credit_extra_time':
        copy.overnight_hourly_premium_type = null;
        copy.overnight_hourly_premium_value = null;
        copy.overnight_hourly_premium_basis = 'base_rate';
        copy.overnight_flat_rate_amount = null;
        break;
      default:
        copy.overnight_hourly_premium_type = null;
        copy.overnight_hourly_premium_value = null;
        copy.overnight_hourly_premium_basis = 'base_rate';
        copy.overnight_flat_rate_amount = null;
        copy.overnight_time_credit_minutes = null;
        break;
    }
    // Preset normalizer for overnight unified recipe:
    //   offset_overtime → floor_source='none',       overtime_offset >= 1
    //   guaranteed_rate → floor_source='fixed_tier', overtime_offset=0, code OR custom
    //   other methods   → floor_source='none',       overtime_offset=0
    switch (copy.overnight_premium_type) {
      case 'offset_overtime':
        copy.overnight_floor_source = 'none';
        if (!copy.overnight_overtime_offset || copy.overnight_overtime_offset < 1) {
          copy.overnight_overtime_offset = 1;
        }
        copy.overnight_floor_tier_code = null;
        copy.overnight_floor_tier_custom_value = null;
        break;
      case 'guaranteed_rate':
        copy.overnight_floor_source = 'fixed_tier';
        copy.overnight_overtime_offset = 0;
        if (copy.overnight_floor_tier_code && copy.overnight_floor_tier_custom_value != null) {
          copy.overnight_floor_tier_custom_value = null;
        }
        break;
      default:
        copy.overnight_floor_source = 'none';
        copy.overnight_overtime_offset = 0;
        copy.overnight_floor_tier_code = null;
        copy.overnight_floor_tier_custom_value = null;
    }
  }

  // Meal Break
  if (copy.apply_meal_surcharge === false) {
    copy.meal_surcharge_type = null;
    copy.meal_premium_type = null;
    copy.meal_premium_value = null;
    copy.meal_premium_basis = 'STRAIGHT_TIME';
    copy.credit_interval_minutes = null;
    copy.time_credit_minutes = null;
    copy.fixed_fee_schedule = null;
    copy.fixed_fee_amount = null;
    copy.step_up_after_minutes = null;
    copy.meal_apply_on_half_days = true;
  } else if (copy.apply_meal_surcharge === true) {
    switch(copy.meal_surcharge_type) {
      case 'hourly_premium':
        // Keep other meal surcharge fields null
        copy.credit_interval_minutes = null;
        copy.time_credit_minutes = null;
        copy.step_up_after_minutes = null;
        copy.fixed_fee_schedule = null;
        copy.fixed_fee_amount = null;
        break;
      case 'offset_overtime':
        copy.meal_premium_type = null;
        copy.meal_premium_value = null;
        copy.meal_premium_basis = 'STRAIGHT_TIME';
        copy.fixed_fee_schedule = null;
        copy.fixed_fee_amount = null;
        copy.time_credit_minutes = null;
        break;
      case 'credit_extra_time':
        // credit_extra_time uses meal_premium_basis for tier selection — preserve it
        copy.meal_premium_type = null;
        copy.meal_premium_value = null;
        copy.step_up_after_minutes = null;
        copy.fixed_fee_schedule = null;
        copy.fixed_fee_amount = null;
        break;
      case 'fixed_fee':
        copy.meal_premium_type = null;
        copy.meal_premium_value = null;
        copy.meal_premium_basis = 'STRAIGHT_TIME';
        copy.credit_interval_minutes = null;
        copy.time_credit_minutes = null;
        copy.step_up_after_minutes = null;
        break;
    }
    // Preset normalizer for meal unified recipe:
    //   offset_overtime → floor_source='none',       overtime_offset >= 1
    //   guaranteed_rate → floor_source='fixed_tier', overtime_offset=0, code OR custom
    //   other methods   → floor_source='none',       overtime_offset=0
    switch (copy.meal_surcharge_type) {
      case 'offset_overtime':
        copy.meal_floor_source = 'none';
        if (!copy.meal_overtime_offset || copy.meal_overtime_offset < 1) {
          copy.meal_overtime_offset = 1;
        }
        copy.meal_floor_tier_code = null;
        copy.meal_floor_tier_custom_value = null;
        break;
      case 'guaranteed_rate':
        copy.meal_floor_source = 'fixed_tier';
        copy.meal_overtime_offset = 0;
        if (copy.meal_floor_tier_code && copy.meal_floor_tier_custom_value != null) {
          copy.meal_floor_tier_custom_value = null;
        }
        break;
      default:
        copy.meal_floor_source = 'none';
        copy.meal_overtime_offset = 0;
        copy.meal_floor_tier_code = null;
        copy.meal_floor_tier_custom_value = null;
    }
  }

  // Holiday Premium
  if (copy.apply_holiday_premium === false) {
    copy.holiday_premium_type = null;
    // NULL-method preset CHECK requires default unified-offset shape.
    copy.holiday_floor_source = 'none';
    copy.holiday_overtime_offset = 0;
    copy.holiday_floor_tier_code = null;
    copy.holiday_floor_tier_custom_value = null;
    copy.holiday_fixed_premium_amount = null;
    copy.holiday_hourly_premium_type = null;
    copy.holiday_hourly_premium_value = null;
    copy.holiday_premium_basis = 'base_rate';
    copy.holiday_apply_on_half_days = true;
  } else if (copy.apply_holiday_premium === true) {
    // Preset normalizer for holiday unified recipe:
    //   offset_overtime → floor_source='none',       overtime_offset >= 1
    //   guaranteed_rate → floor_source='fixed_tier', overtime_offset=0, code OR custom
    //   other methods   → floor_source='none',       overtime_offset=0
    switch (copy.holiday_premium_type) {
      case 'offset_overtime':
        copy.holiday_floor_source = 'none';
        if (!copy.holiday_overtime_offset || copy.holiday_overtime_offset < 1) {
          copy.holiday_overtime_offset = 1;
        }
        copy.holiday_floor_tier_code = null;
        copy.holiday_floor_tier_custom_value = null;
        break;
      case 'guaranteed_rate':
        copy.holiday_floor_source = 'fixed_tier';
        copy.holiday_overtime_offset = 0;
        if (copy.holiday_floor_tier_code && copy.holiday_floor_tier_custom_value != null) {
          copy.holiday_floor_tier_custom_value = null;
        }
        break;
      default:
        copy.holiday_floor_source = 'none';
        copy.holiday_overtime_offset = 0;
        copy.holiday_floor_tier_code = null;
        copy.holiday_floor_tier_custom_value = null;
    }
    if (copy.holiday_premium_type !== 'fixed_dollar') {
      copy.holiday_fixed_premium_amount = null;
    }
    if (copy.holiday_premium_type !== 'hourly_premium') {
      copy.holiday_hourly_premium_type = null;
      copy.holiday_hourly_premium_value = null;
      copy.holiday_premium_basis = 'base_rate';
    }
  }

  // You can add more sanitization logic here for other fields if needed,
  // mirroring sanitizeUserConfig.ts for consistency.

  return copy;
};

export const buildSavePayload = (
  formData: ClientFormData,
  customizedSections: CustomizedSections,
  isEditing: boolean
): Partial<ClientFormData> => {
  if (isEditing) {
    return sanitizeClientData(formData);
  }

  // For new clients, send only the name and customized fields
  const payload: Partial<ClientFormData> = {
    name: formData.name,
    description: formData.description,
    domain: formData.domain,
    physical_address: formData.physical_address,
    billing_address: formData.billing_address,
    accounts_payable_email: formData.accounts_payable_email,
  };

  // Use centralized field definitions to build payload
  for (const section of Object.keys(customizedSections)) {
    if (customizedSections[section as keyof typeof customizedSections]) {
      if (section === 'billing') {
        // 'billing' is a combination of 'dayRate' and 'travel'
        for (const key of ['dayRate', 'travel', 'breaks']) {
          const fields = SECTION_FIELDS[key];
          if (fields) {
            for (const field of fields) {
              if (field in formData) {
                (payload as Record<string, unknown>)[field] = formData[field];
              }
            }
          }
        }
      } else {
        const fields = SECTION_FIELDS[section];
        if (fields) {
          for (const field of fields) {
            if (field in formData) {
              (payload as Record<string, unknown>)[field] = formData[field];
            }
          }
        }
      }
    }
  }

  return sanitizeClientData(payload);
};
